import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CopyableReadonlyField } from '@/components/forms/copyable-readonly-field'
import { ConfirmationDialog } from '@/components/overlays/confirmation-dialog'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'

import type { OAuthResourceCreated, OidcProviderConfiguration } from './api'

export function OAuthResourceCredentialsModal({
  onClose,
  onRetryProvider,
  provider,
  providerError = false,
  providerLoading = false,
  resource,
}: {
  onClose: () => void
  onRetryProvider?: () => void
  provider: OidcProviderConfiguration | null
  providerError?: boolean
  providerLoading?: boolean
  resource: OAuthResourceCreated | null
}) {
  const { t } = useTranslation('adminIdp')
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false)
  const [copiedSecret, setCopiedSecret] = useState<string | null>(null)
  const [copyFailed, setCopyFailed] = useState(false)
  const packageCopied = copiedSecret !== null && resource?.introspection_secret === copiedSecret
  const integrationPackage = resource
    ? JSON.stringify(
        {
          introspection_client_id: resource.introspection_client_id,
          introspection_endpoint: provider?.introspection_endpoint ?? '',
          introspection_secret: resource.introspection_secret,
          issuer: provider?.issuer ?? '',
          resource: resource.resource_uri,
          token_format: 'opaque',
        },
        null,
        2,
      )
    : ''

  const copy = async (value: string, isPackage = false) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopyFailed(false)
      if (isPackage && resource) setCopiedSecret(resource.introspection_secret)
    } catch {
      setCopyFailed(true)
    }
  }
  const close = () => {
    setCloseConfirmOpen(false)
    onClose()
  }
  const requestClose = () => {
    if (resource && !packageCopied) {
      setCloseConfirmOpen(true)
      return
    }
    close()
  }

  if (resource && closeConfirmOpen) {
    return (
      <ConfirmationDialog
        cancelLabel={t('actions.continueSaving')}
        confirmLabel={t('actions.closeAnyway')}
        description={t('resources.credentials.closeConfirm')}
        onCancel={() => setCloseConfirmOpen(false)}
        onConfirm={close}
        open
        title={t('resources.credentials.closeConfirmTitle')}
      />
    )
  }

  return (
    <Modal
      className="max-w-[760px]"
      closeLabel={t('actions.cancel')}
      footer={
        <>
          <Button
            disabled={!provider}
            onClick={() => void copy(integrationPackage, true)}
            variant="secondary"
          >
            {t('resources.credentials.copyPackage')}
          </Button>
          <Button onClick={requestClose}>{t('actions.done')}</Button>
        </>
      }
      onClose={requestClose}
      open={Boolean(resource)}
      subtitle={t('resources.credentials.oneTimeHint')}
      title={t('resources.credentials.title')}
    >
      {resource ? (
        <div className="grid max-h-[62vh] gap-4 overflow-y-auto pr-1">
          {providerError ? (
            <div
              className="flex items-center justify-between gap-3 rounded-md border border-[var(--color-danger)] px-3 py-2 text-sm text-[var(--color-danger)]"
              role="alert"
            >
              <span>{t('resources.credentials.providerLoadFailed')}</span>
              {onRetryProvider ? (
                <Button onClick={onRetryProvider} size="sm" variant="secondary">
                  {t('resources.credentials.retryProvider')}
                </Button>
              ) : null}
            </div>
          ) : providerLoading && !provider ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('resources.credentials.providerLoading')}
            </p>
          ) : null}
          {copyFailed ? (
            <p className="text-sm text-[var(--color-danger)]" role="status">
              {t('resources.credentials.copyFailed')}
            </p>
          ) : null}
          <CopyableReadonlyField
            copyLabel={t('actions.copy')}
            label={t('resources.fields.resourceUri')}
            monospace
            onCopy={() => void copy(resource.resource_uri)}
            showLabel
            value={resource.resource_uri}
          />
          <CopyableReadonlyField
            copyLabel={t('actions.copy')}
            label={t('parameters.introspectionEndpoint')}
            monospace
            onCopy={() => void copy(provider?.introspection_endpoint ?? '')}
            showLabel
            value={provider?.introspection_endpoint ?? ''}
          />
          <CopyableReadonlyField
            copyLabel={t('actions.copy')}
            label={t('resources.fields.introspectionClientId')}
            monospace
            onCopy={() => void copy(resource.introspection_client_id)}
            showLabel
            value={resource.introspection_client_id}
          />
          <CopyableReadonlyField
            copyLabel={t('actions.copy')}
            label={t('resources.fields.introspectionSecret')}
            monospace
            onCopy={() => void copy(resource.introspection_secret)}
            showLabel
            value={resource.introspection_secret}
          />
        </div>
      ) : null}
    </Modal>
  )
}
